import type { FetchError } from 'ofetch'

type LogError = MaybeRef<FetchError | Error | null | undefined>
type LogErrorOverloads = {
  (locationOrError: LogError): void
  (locationOrError: string, message: string, errorOrMessage: LogError): void
}

/**
 * Log an API/fetch error on a single line.
 *
 * Only logs when there actually is an error: the previous version wrote a
 * line on every call (~190k "from actions" lines a day in the PM2 error log
 * without any error attached) and dumped the whole FetchError object
 * (request, response, stack) over dozens of lines.
 */
export const logError: LogErrorOverloads = (
  locationOrError: LogError | string,
  message?: string,
  errorOrMessage?: LogError,
) => {
  const error = unref(
    typeof locationOrError === 'string' ? errorOrMessage : locationOrError,
  )

  if (!error) return

  const prefix =
    typeof locationOrError === 'string'
      ? `[${locationOrError}] ${message ?? ''}`.trimEnd()
      : '[error]'

  if (isFetchError(error)) {
    const status = error.statusCode ?? error.response?.status ?? '-'
    const url = error.request ? String(error.request) : '-'
    const detail =
      error.data && typeof error.data === 'object' && 'message' in error.data
        ? String((error.data as { message: unknown }).message)
        : (error.statusMessage ?? error.message)

    console.error(`${prefix} ${status} ${url} ${detail}`)
    return
  }

  console.error(prefix, error instanceof Error ? error.message : error)
}

const isFetchError = (error: unknown): error is FetchError =>
  typeof error === 'object' &&
  error !== null &&
  ('statusCode' in error || 'request' in error || 'response' in error)
